// Gesture recognition and stroke classification for Hexcrawl

import type { Stroke, StrokePoint, Point } from "../types.ts";

/** Type of stroke based on gesture recognition */
export type StrokeType = 'closed' | 'line' | 'scribble';

/** Bounding box for a stroke */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Detects if a stroke forms a closed shape.
 *
 * @param stroke - The stroke to analyze
 * @param threshold - Distance threshold as percentage of shape size (default 0.15 = 15%)
 * @returns true if the stroke is closed
 */
export function isClosed(stroke: Stroke, threshold: number = 0.15): boolean {
  // Need at least 10 points to form a meaningful closed shape
  if (stroke.points.length < 10) {
    return false;
  }

  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];

  // Calculate bounding box to determine shape size
  const bbox = strokeBoundingBox(stroke);
  const size = Math.max(bbox.width, bbox.height);

  // Distance between start and end points
  const distance = Math.hypot(last.x - first.x, last.y - first.y);

  // Closed if the distance is less than threshold percentage of shape size
  return distance < size * threshold;
}

/**
 * Detects scribble gestures (typically used for deletion).
 * Uses getStrokeMetrics internally for calculations.
 */
export function isScribble(stroke: Stroke): boolean {
  if (stroke.points.length < 10) return false;

  const metrics = getStrokeMetrics(stroke);
  if (metrics.diagonal < 15) return false;

  // Scribble detection criteria:
  const hasManyTurns = metrics.significantTurns >= 3;
  const isFastWithTurns = metrics.avgVelocity > 0.3 && metrics.significantTurns >= 2;
  const hasBackAndForth = metrics.pathRatio > 2.5 && metrics.significantTurns >= 1;
  const hasVeryHighPathRatio = metrics.pathRatio > 4;
  const isCircularScribble = metrics.pathRatio > 3 && metrics.pathLength > 200;

  return hasManyTurns || isFastWithTurns || hasBackAndForth || hasVeryHighPathRatio || isCircularScribble;
}

/**
 * Get debug metrics for a stroke (useful for tuning thresholds)
 */
export function getStrokeMetrics(stroke: Stroke): {
  pointCount: number;
  pathLength: number;
  diagonal: number;
  pathRatio: number;
  significantTurns: number;
  avgVelocity: number;
  startEndDistance: number;
  closedThreshold: number;
  wouldBeScribble: string;
} {
  const bbox = strokeBoundingBox(stroke);
  const diagonal = Math.hypot(bbox.width, bbox.height);
  const size = Math.max(bbox.width, bbox.height);

  let pathLength = 0;
  const velocities: number[] = [];

  for (let i = 1; i < stroke.points.length; i++) {
    const dx = stroke.points[i].x - stroke.points[i - 1].x;
    const dy = stroke.points[i].y - stroke.points[i - 1].y;
    const dist = Math.hypot(dx, dy);
    pathLength += dist;

    const dt = stroke.points[i].timestamp - stroke.points[i - 1].timestamp;
    if (dt > 0) {
      velocities.push(dist / dt);
    }
  }

  const avgVelocity = velocities.length > 0
    ? velocities.reduce((a, b) => a + b, 0) / velocities.length
    : 0;

  // Count sharp reversals (angle > 120 degrees)
  let significantTurns = 0;
  for (let i = 2; i < stroke.points.length; i++) {
    const dx1 = stroke.points[i - 1].x - stroke.points[i - 2].x;
    const dy1 = stroke.points[i - 1].y - stroke.points[i - 2].y;
    const dx2 = stroke.points[i].x - stroke.points[i - 1].x;
    const dy2 = stroke.points[i].y - stroke.points[i - 1].y;

    const len1 = Math.hypot(dx1, dy1);
    const len2 = Math.hypot(dx2, dy2);

    if (len1 > 0.5 && len2 > 0.5) {
      const dot = (dx1 * dx2 + dy1 * dy2) / (len1 * len2);
      if (dot < -0.5) significantTurns++;  // >120 degree turn
    }
  }

  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];
  const startEndDistance = Math.hypot(last.x - first.x, last.y - first.y);

  const pathRatio = diagonal > 0 ? pathLength / diagonal : 0;

  // Explain why it would/wouldn't be a scribble (updated for relaxed thresholds)
  const reasons: string[] = [];
  if (significantTurns >= 4) reasons.push(`turns>=4`);
  if (avgVelocity > 0.4 && significantTurns >= 3) reasons.push(`fast(v>0.4 & turns>=3)`);
  if (pathRatio > 3.5 && significantTurns >= 2) reasons.push(`backAndForth(path>3.5 & turns>=2)`);

  return {
    pointCount: stroke.points.length,
    pathLength: Math.round(pathLength),
    diagonal: Math.round(diagonal),
    pathRatio: Math.round(pathRatio * 100) / 100,
    significantTurns,
    avgVelocity: Math.round(avgVelocity * 1000) / 1000, // px/ms
    startEndDistance: Math.round(startEndDistance),
    closedThreshold: Math.round(size * 0.15),
    wouldBeScribble: reasons.length > 0 ? reasons.join(', ') : 'no',
  };
}

/**
 * Classifies a stroke into one of three types: closed, line, or scribble.
 *
 * @param stroke - The stroke to classify
 * @returns The stroke type
 */
export function classifyStroke(stroke: Stroke): StrokeType {
  // Check for scribble first - scribbles are chaotic with many direction changes
  // A clean closed shape (like a circle) won't have enough direction changes
  if (isScribble(stroke)) {
    return 'scribble';
  }

  // Then check if it's a closed shape (room)
  if (isClosed(stroke)) {
    return 'closed';
  }

  // Otherwise it's a line (corridor)
  return 'line';
}

/**
 * Calculates the bounding box of a stroke.
 *
 * @param stroke - The stroke to analyze
 * @returns Bounding box with x, y, width, and height
 */
export function strokeBoundingBox(stroke: Stroke): BoundingBox {
  if (stroke.points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of stroke.points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Calculates the centroid (center point) of a stroke.
 *
 * @param stroke - The stroke to analyze
 * @returns The center point of the stroke
 */
export function strokeCentroid(stroke: Stroke): Point {
  if (stroke.points.length === 0) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;

  for (const point of stroke.points) {
    sumX += point.x;
    sumY += point.y;
  }

  return {
    x: sumX / stroke.points.length,
    y: sumY / stroke.points.length
  };
}

/**
 * Calculate the "closeness" ratio of a stroke (0 = open, 1 = closed)
 * This provides a continuous measure rather than a binary threshold
 */
export function getCloseness(stroke: Stroke): number {
  if (stroke.points.length < 5) return 0;

  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];
  const bbox = strokeBoundingBox(stroke);
  const size = Math.max(bbox.width, bbox.height);

  if (size < 10) return 0;

  const distance = Math.hypot(last.x - first.x, last.y - first.y);
  const ratio = distance / size;

  // Map to 0-1 where 1 = closed
  // ratio of 0 = perfectly closed = 1.0
  // ratio of 0.15 = threshold = ~0.7
  // ratio of 0.5 = pretty open = ~0.0
  return Math.max(0, Math.min(1, 1 - ratio * 2));
}

/**
 * Auto-close a stroke by adding points to connect end to start.
 * Only does this if the stroke is "almost closed" (closeness > threshold)
 */
export function autoCloseStroke(stroke: Stroke, threshold: number = 0.5): Stroke {
  const closeness = getCloseness(stroke);

  if (closeness >= 0.85) {
    // Already closed enough, just mark it
    return { ...stroke, isClosed: true };
  }

  if (closeness < threshold) {
    // Too open to auto-close
    return stroke;
  }

  // Add interpolated points from last to first
  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];
  const distance = Math.hypot(last.x - first.x, last.y - first.y);
  const numSteps = Math.max(2, Math.ceil(distance / 5)); // One point every ~5px

  const closingPoints: StrokePoint[] = [];
  for (let i = 1; i <= numSteps; i++) {
    const t = i / numSteps;
    closingPoints.push({
      x: last.x + (first.x - last.x) * t,
      y: last.y + (first.y - last.y) * t,
      pressure: (last.pressure + first.pressure) / 2,
      timestamp: last.timestamp + i, // Arbitrary small increment
    });
  }

  return {
    points: [...stroke.points, ...closingPoints],
    isClosed: true,
  };
}

/**
 * Enhanced stroke classification with confidence scores
 */
export interface ClassificationResult {
  type: StrokeType;
  confidence: number;
  closeness: number;
  metrics: ReturnType<typeof getStrokeMetrics>;
  autoCloseApplied: boolean;
}

/**
 * Classify a stroke with detailed results including confidence
 */
export function classifyStrokeDetailed(stroke: Stroke): ClassificationResult {
  const metrics = getStrokeMetrics(stroke);
  const closeness = getCloseness(stroke);

  // Check scribble first (lowered threshold from 0.6 to 0.5)
  const scribbleScore = calculateScribbleScore(metrics);
  if (scribbleScore > 0.5) {
    return {
      type: 'scribble',
      confidence: scribbleScore,
      closeness,
      metrics,
      autoCloseApplied: false,
    };
  }

  // Check for closed shape
  if (closeness > 0.85) {
    // Clearly closed
    return {
      type: 'closed',
      confidence: 0.95,
      closeness,
      metrics,
      autoCloseApplied: false,
    };
  }

  if (closeness > 0.5) {
    // Almost closed - would benefit from auto-close
    return {
      type: 'closed',
      confidence: 0.5 + closeness * 0.4,
      closeness,
      metrics,
      autoCloseApplied: true,
    };
  }

  // It's a line
  return {
    type: 'line',
    confidence: 0.8,
    closeness,
    metrics,
    autoCloseApplied: false,
  };
}

/**
 * Calculate a scribble score from 0-1
 */
function calculateScribbleScore(metrics: ReturnType<typeof getStrokeMetrics>): number {
  let score = 0;

  // Many turns is a strong signal (relaxed threshold)
  if (metrics.significantTurns >= 4) score += 0.5;
  else if (metrics.significantTurns >= 3) score += 0.3;
  else if (metrics.significantTurns >= 2) score += 0.15;

  // High velocity with turns (relaxed threshold)
  if (metrics.avgVelocity > 0.4 && metrics.significantTurns >= 2) score += 0.3;

  // Back-and-forth motion (relaxed threshold)
  if (metrics.pathRatio > 3.5 && metrics.significantTurns >= 2) score += 0.35;
  else if (metrics.pathRatio > 2.5) score += 0.2;

  // Circular/spiral scribbles: high path ratio + long path even without sharp turns
  // This catches scribbles that loop around without sharp reversals
  if (metrics.pathRatio > 3 && metrics.pathLength > 200) score += 0.55;

  return Math.min(1, score);
}
